import { CycleDetailsItems } from '@/api';
import { useAppDispatch } from '@/app/store';
import { UIBox } from '@/components/ui/Box';
import { UIButton } from '@/components/ui/Button';
import { UICheckbox } from '@/components/ui/Checkbox';
import { StyledModalProps, UIModal } from '@/components/ui/Modal';
import { Typography } from '@/components/ui/Typography';
import { setDecodedItemsConfirmed } from '@/features/cycleCount/cycleCountSlice';
import { useSelector } from '@/hooks/useSelector';

import { palette } from '@/theme';
import { IconButton } from '@material-ui/core';
import ErrorIcon from '@material-ui/icons/Error';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import React, { FC, memo, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';

export interface SohaKickoffModalProps {
  open: boolean;
  title?: string;
  message?: string;
  decodedItems?: [];
  onConfirm: () => void;
  onClose: () => void;
  hideCheck?: boolean;
}

//#region - Styled Components

const StyledWrapper = styled(UIBox)`
  width: 100%;
  padding: 24px;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;

  flex-direction: column;
`;

const StyledCloseIcon = styled(IconButton)`
  align-self: baseline;

  svg {
    fill: ${palette.colors.primary};
  }
`;

const StyledConfirmModalContent = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledConfirmModalTextWrapper = styled(UIBox)`
  flex-direction: column;

  h1 {
    margin: 0 0 8px;
  }
`;

const StyledErrorOutlineIcon = styled(ErrorIcon)`
  margin-right: 8px;
  height: 24px;
  width: 24px;
`;

const Header = styled.div`
  display: flex;
  width: 100%;
`;
const Content = styled.div`
  height: 100%;
  width: 100%;

  //max-height: 450px;
  //overflow-y: auto;
  display: flex;
  align-items: start;
  justify-content: center;
`;
const Footer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100%;
  justify-content: center;
  align-items: center;
  gap: 24px;
`;

const CheckConfirmContainer = styled.div`
  display: flex;
  width: 100%;
  justify-content: center;
  align-items: center;
  cursor: pointer;
  padding-top: 16px;
`;
//#endregion

export const SohaKickoffModal: FC<
  SohaKickoffModalProps & Partial<StyledModalProps>
> = memo(
  ({
    onClose,
    onConfirm,
    open,
    title,
    message,
    hideCheck,
    ...styledProps
  }): JSX.Element => {
    const { t } = useTranslation();
    const dispatch = useAppDispatch();

    const [checkConfirm, setCheckConfirm] = useState<boolean>(false);

    const { decodedItems } = useSelector(state => state.cycleCount.products);

    useEffect(() => {
      const items: CycleDetailsItems[] = [];
      decodedItems?.forEach(item => {
        items.push(item);
      });
      dispatch(setDecodedItemsConfirmed({ decodedItemsConfirmed: items }));
    }, [dispatch, decodedItems]);

    return (
      <UIModal
        open={open}
        $width="50vw"
        $height={'fit-content'}
        onClose={onClose}
        {...styledProps}
      >
        <StyledWrapper>
          <Header>
            <StyledConfirmModalContent>
              <StyledConfirmModalTextWrapper>
                <Typography
                  size="lg"
                  font="heavy"
                  as="h1"
                  style={{ display: 'flex', alignItems: 'center' }}
                >
                  <StyledErrorOutlineIcon htmlColor={palette.colors.yellow} />
                  {title}
                </Typography>
              </StyledConfirmModalTextWrapper>
            </StyledConfirmModalContent>
            <StyledCloseIcon onClick={onClose}>
              <HighlightOffIcon />
            </StyledCloseIcon>
          </Header>
          <Content>
            <Typography
              size="lg"
              font="heavy"
              as="h2"
              style={{
                display: 'flex',
                alignItems: 'center',
                lineHeight: '32px',
              }}
            >
              {message}
            </Typography>
          </Content>
          <Footer>
            {!hideCheck && (
              <CheckConfirmContainer
                onClick={(): void => setCheckConfirm(!checkConfirm)}
              >
                <UICheckbox checked={checkConfirm} />
                <Typography size="lg" font="heavy" as="h1">
                  {t('soha.kickoff.confirm')}
                </Typography>
              </CheckConfirmContainer>
            )}
            <UIButton
              onClick={onConfirm}
              label={
                hideCheck ? t('soha.kickoff.open') : t('soha.kickoff.label')
              }
              outlined={true}
              disabled={hideCheck ? false : !checkConfirm}
            />
          </Footer>
        </StyledWrapper>
      </UIModal>
    );
  }
);

SohaKickoffModal.displayName = 'SohaKickoffModal';

export default SohaKickoffModal;
